import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { matchesSearch } from '../../../core/utils/search-text';

/** Option d'un sélecteur recherchable. */
export interface SelectItem {
  id: number;
  label: string;
}

/**
 * Sélecteur déroulant avec recherche, réutilisable.
 *
 * <p>Même principe que le filtre de la banque de questions : un déclencheur qui
 * affiche la valeur, un panneau avec champ de recherche filtrant la liste,
 * fermeture au clic-dehors. Entièrement en jetons de thème (3 tiers + mode
 * sombre) et RTL natif.</p>
 */
@Component({
  selector: 'app-searchable-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchableSelectComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly options = input<SelectItem[]>([]);
  readonly value = input<number | null>(null);
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  /** Classe FontAwesome d'icône de tête, ex. `'fa-solid fa-route'`. */
  readonly icon = input<string | null>(null);
  /**
   * Libellé de l'entrée « aucun filtre » (ex. « Tous les parcours »), placée en
   * tête de liste et associée à la valeur `null`. Laisser vide pour un
   * sélecteur où une valeur est obligatoire : l'entrée n'est alors pas rendue.
   */
  readonly allLabel = input<string | null>(null);

  readonly valueChange = output<number | null>();

  readonly open = signal(false);
  readonly search = signal('');

  readonly selectedLabel = computed(() => {
    const value = this.value();
    // `null` ne signifie « tout » que si l'appelant a fourni le libellé ;
    // sinon on retombe sur le placeholder (rien de sélectionné).
    if (value == null) return this.allLabel() ?? '';
    return this.options().find((o) => o.id === value)?.label ?? '';
  });

  readonly filtered = computed(() => {
    const term = this.search();
    if (!term.trim()) return this.options();
    return this.options().filter((o) => matchesSearch(term, o.label));
  });

  /** L'entrée « tout » reste visible tant que la recherche la concerne. */
  readonly showAll = computed(() => {
    const all = this.allLabel();
    if (!all) return false;
    return matchesSearch(this.search(), all);
  });

  toggle(): void {
    if (this.disabled()) return;
    this.open.update((v) => !v);
    if (!this.open()) this.search.set('');
  }

  select(option: SelectItem): void {
    this.open.set(false);
    this.search.set('');
    if (option.id !== this.value()) this.valueChange.emit(option.id);
  }

  /** Efface la facette : le parent reçoit `null` et cesse de filtrer dessus. */
  selectAll(): void {
    this.open.set(false);
    this.search.set('');
    if (this.value() != null) this.valueChange.emit(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
      this.search.set('');
    }
  }
}
